import { Route, Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { authMatcher, loginRequiredGuard } from '@processpuzzle/auth';
import { ORG_ADMIN_ROUTES } from '@processpuzzle/org-admin';
import { AdministrationDeniedComponent } from './administration/administration-denied.component';
import { orgAdminGuard } from './administration/org-admin.guard';
import { pageRevisionGuard } from './runtime/page-revision';
import { publishedApplicationGuard } from './runtime/published-application.guard';
import { RuntimeHostComponent } from './runtime/runtime-host.component';
import { WorkspaceShellComponent } from './workspace/workspace-shell.component';

/**
 * Path segments this application uses for itself, which therefore cannot be read as an
 * organization key.
 *
 * Much shorter than `processpuzzle-biz-frontend`'s, and necessarily so: every non-`auth` URL in
 * this application *is* an organization's, because there is no public surface here at all. A
 * marketing page, a pricing page and a sign-up funnel are Biz's job; this application is only ever
 * reached by somebody who already has an account.
 *
 * `auth` is the one exception, and it is load-bearing rather than tidy. `looksLikeOrgKey('auth')`
 * is true, so without this list the Keycloak callback would be read as an organization named
 * `auth`, and the routes would be built for a tenant that does not exist — leaving the user staring
 * at the no-organization page immediately after a successful login.
 */
export const RESERVED_PATH_SEGMENTS = ['auth'];

/**
 * The application's routes, as a function of the organization the URL named.
 *
 * **A function rather than a constant, and the branch is mounted under a literal `<orgKey>` path
 * rather than a `:orgKey` parameter.** Two reasons, the same two `processpuzzle-biz-frontend`
 * records:
 *
 * - `ORG_ADMIN_ORG_KEY` is injected while `OrganizationUserService` — `providedIn: 'root'` — is
 *   constructed, which happens before any route activates. It has to be a value settled at
 *   bootstrap, so the routes are built from the same value rather than from a parameter read later.
 * - Because the path is literal, a URL naming a *different* organization matches nothing and the
 *   browser reloads. That is the wanted behaviour: switching organizations must re-bootstrap, or
 *   org B's screens would be rendered against the key the application was started with.
 *
 * **Two layout routes, two kinds of chrome.** The workspace — designer, administration, and the
 * no-organization and auth pages — is framed by `WorkspaceShellComponent`, the platform's chrome. Its
 * route has no path segment of its own, so the URLs are unchanged. Everything else under
 * `/<orgKey>` is the organization's application: `RuntimeHostComponent` renders the revision its
 * designer last published, whose routes `publishedApplicationGuard` registers as the host's children.
 * The workspace comes first, so `/<orgKey>/design` and `/<orgKey>/admin` are never offered to the
 * application — a definition cannot shadow them.
 *
 * **What the shell does not carry.** No platform-admin routes, per
 * `docs/processpuzzle-architecture-summary.md` §6 — "shared UI libs, minus platform-admin". Those
 * screens administer *every* tenant and belong to staff; mounting them here would put the tenant
 * registry behind a customer's login.
 *
 * The workspace header and sidenav render the titled routes — see `navigableRoutes` — which is why
 * every navigable route carries a `title` and the auth matcher and the redirects deliberately do not.
 *
 * **Every organization route requires a login**: `loginRequiredGuard` on the workspace's, and the
 * same login inside `publishedApplicationGuard` for the application's, which needs a session before
 * it can read what was published. Either sends a visitor without a session to Keycloak and back to
 * the page they asked for — which, arriving from Customer Home in Biz, is no visible login at all:
 * both log in against `processpuzzle-custom`, and Keycloak's session is per realm.
 */
export function createAppRoutes(orgKey?: string): Route[] {
  const organizationWorkspace: Route[] = orgKey
    ? [
        {
          // **The designer — the reason a customer has an application at all.** Entities,
          // Documents, Rules, States, Workflows and the Application shell, as `DESIGN_ROUTES`
          // ships them, so a customer builds their own app rather than being handed a fixed one.
          //
          // Reached from the design button, not from the sidenav: it is a *mode* rather than a
          // section, and `DesignRouteService` swaps the workspace's sidenav and header title when the
          // URL enters it. That is also why this route carries no `title` or `menuTitle`: a titled
          // route would put the designer in the ordinary navigation beside the screens it designs.
          //
          // The prefix problem this creates is settled in `app.config.ts` by
          // `DESIGN_ROUTE_PREFIX`. Everything under here addresses itself as `/design/...`
          // absolutely, and here that is `/{orgKey}/design/...`.
          //
          // It shows the *draft* definitions, the application the released ones; arriving here from a page of the
          // application loads the page anew, so that no cache holds both (`PAGE_REVISION`).
          path: `${orgKey}/design`,
          canMatch: [pageRevisionGuard('draft')],
          canActivate: [loginRequiredGuard],
          loadChildren: () => import('@processpuzzle/design').then((design) => design.DESIGN_ROUTES),
          providers: [provideTranslocoScope({ scope: 'design', alias: 'design' })],
        },
        {
          path: `${orgKey}/admin`,
          title: 'ProcessPuzzle - Administration',
          canMatch: [orgAdminGuard],
          canActivate: [loginRequiredGuard],
          data: { icon: 'admin_panel_settings', menuTitle: 'admin.administration' },
          children: ORG_ADMIN_ROUTES,
        },
        {
          // The same path, for a member without `org-admin`: every address under it says why there is
          // nothing to administer here, rather than showing the backend's 403 as empty lists. Untitled,
          // so the navigation offers the branch above once and not this one beside it.
          path: `${orgKey}/admin`,
          canActivate: [loginRequiredGuard],
          children: [{ path: '**', component: AdministrationDeniedComponent }],
        },
      ]
    : [];

  const application: Route[] = orgKey
    ? [
        {
          // `/<orgKey>` and everything below it that the workspace did not claim: the organization's
          // application, as published. Customer Home's "Open my application" link lands here.
          path: orgKey,
          title: 'ProcessPuzzle - Home',
          // First in the workspace navigation: the application is what the members came for.
          data: { icon: 'home', menuTitle: 'home', navOrder: -1 },
          component: RuntimeHostComponent,
          // Released definitions only, so a page of the designer, which read drafts, is left for a new one first.
          canMatch: [pageRevisionGuard('released'), publishedApplicationGuard(unpublishedApplication())],
        },
      ]
    : [];

  return [
    // Ahead of the workspace, and not after it: the workspace route is `''` with children, and Angular
    // matches that route against an empty URL even when none of its children does, so a redirect placed
    // after it is never reached and `/` rendered the shell with an empty page.
    // To the application, not to a page inside it: which pages exist is the published definition's
    // business, and the host lands on its first one.
    { path: '', pathMatch: 'full', redirectTo: orgKey ?? 'no-organization' },
    {
      path: '',
      component: WorkspaceShellComponent,
      children: [
        ...organizationWorkspace,
        {
          // Reached when the URL names no organization — `/` typed by hand, or a bookmark that lost
          // its first segment. Not a redirect to anything: this application has no organization-less
          // surface to redirect *to*, and silently sending somebody to an arbitrary tenant would be
          // worse than telling them the address is incomplete.
          path: 'no-organization',
          loadComponent: () => import('./no-organization/no-organization.component').then((component) => component.NoOrganizationComponent),
        },
        {
          // Matches any URL containing `auth`, which is how the library's callback and profile routes
          // are reached regardless of the provider's redirect shape.
          matcher: authMatcher,
          loadChildren: () => import('@processpuzzle/auth/feature').then((routes) => routes.authRoutes),
        },
      ],
    },
    ...application,
    // Anything else is a path inside an organization this bundle was not started for, a page the
    // published application no longer has, or a typo.
    { path: '**', redirectTo: orgKey ?? 'no-organization' },
  ];
}

/**
 * What the application's URL shows while its designer has published nothing — every newly seeded
 * organization's state: Customer Home's cards into the designer and the users, and a hint that the
 * application appears here once it is published.
 *
 * At `home`, which is also where the design button's way back and older links point; once something
 * is published they reach the application's own first page through the catch-all instead.
 */
function unpublishedApplication(): Routes {
  return [
    {
      path: 'home',
      loadComponent: () => import('./home/customer-home.component').then((component) => component.CustomerHomeComponent),
    },
    { path: '', pathMatch: 'full', redirectTo: 'home' },
  ];
}
